import { hasExplicitLogout } from "../utils/authStorage"
import { mergeSiteData } from "../utils/mergeSiteData"
import { getAllOutboxItems, deleteOutboxItem, updateOutboxItem, getCachedData, putCachedData } from "./offlineDb"
import { applySyncedKeyIssue } from "../redux/slices/keyIssueMatrixSlice"
import { cacheImageBlob } from "./imageCache"
import { authenticatedSession, checkSyncResponse, orderSyncItems, dependentIds, providedIds } from "./syncPolicy"
import {
  setOnlineStatus,
  beginSyncRun,
  updateSyncOperation,
  setLastSyncSucceededAt,
  rememberResolvedIds,
  setOutboxItems,
  upsertOutboxItem,
  removeOutboxItem,
  setIsSyncing,
  setHasPendingSyncPrompt,
} from "../redux/slices/offlineSlice"
import { retryWithBackoff } from "../utils/retryWithBackoff"
import { uploadFileDirectToS3 } from "../utils/presignedUpload"
import { addSyncedFile, setFileQueueStatus } from "../redux/slices/fileSlice"
import { addSyncedReport, replaceSyncedReportFile } from "../redux/slices/auditReportSlice"
import { addSyncedSite, hydratePendingSites } from "../redux/slices/siteDataSlice"
import { addSyncedObservation } from "../redux/slices/observationSlice"
import { addSyncedWork } from "../redux/slices/workSlice"
import { fetchAllReportsWithData } from "../redux/slices/auditReportSlice"

// Authenticated sessions sync on reconnect; failures remain durable in IndexedDB.

let listenersRegistered = false
let draining = false

const AUTH_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8005/api"

/**
 * Phase 5 — the outbox may sit for hours (an auditor loses signal for a
 * whole site visit), long enough for the session that queued a request to
 * look expired by the time it's replayed. Rather than dropping the queued
 * work, try a single silent refresh and replay once with a fresh session
 * before treating it as a real failure.
 */
async function attemptSilentRefresh() {
  if (hasExplicitLogout()) return false
  try {
    const response = await fetch(`${AUTH_BASE_URL}/auth/refresh-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
    return response.ok
  } catch (error) {
    return false
  }
}

async function replayOutboxItem(item, { allowAuthRetry = true } = {}) {
  const { url, method = "GET", headers = {}, body } = item

  const response = await retryWithBackoff(
    () =>
      fetch(url, {
        method,
        headers,
        body,
        credentials: "include",
      }),
    { retries: 2 },
  )

  if (response.status === 401 && allowAuthRetry) {
    const refreshed = await attemptSilentRefresh()
    if (refreshed) {
      return replayOutboxItem(item, { allowAuthRetry: false })
    }
  }

  await checkSyncResponse(response)

  return response
}

/**
 * File-upload outbox items don't have a `url`/`body` shaped request to
 * replay generically — they carry the raw `file` + `metadata` that was
 * captured at queue time. Replaying them means actually running the same
 * direct-to-S3 + `/presign/complete` pipeline used for a live upload, so
 * the S3 object AND the database file record both get created, instead of
 * the item being marked failed forever.
 */
async function replayFileUploadItem(item, { allowAuthRetry = true, resolveId = (value) => value } = {}) {
  const rawMetadata = item.metadata || {}
  const metadata = {
    ...rawMetadata,
    siteId: resolveId(rawMetadata.siteId),
    reportId: resolveId(rawMetadata.reportId),
    entityId: resolveId(rawMetadata.entityId),
    filterData: item.filterData ?? rawMetadata.filterData ?? {},
    metadata: {
      ...(rawMetadata.metadata || {}),
      siteId: resolveId(rawMetadata.metadata?.siteId),
      reportId: resolveId(rawMetadata.metadata?.reportId),
      entityId: resolveId(rawMetadata.metadata?.entityId),
    },
  }

  try {
    return await uploadFileDirectToS3(item.file, metadata, { resumeKey: item.clientId })
  } catch (error) {
    if (error?.status === 401 && allowAuthRetry) {
      const refreshed = await attemptSilentRefresh()
      if (refreshed) {
        return replayFileUploadItem(item, { allowAuthRetry: false, resolveId })
      }
    }
    throw error
  }
}

export async function drainOutbox(dispatch, { getState, itemId, automatic = false } = {}) {
  const canContinue = () => (!getState || authenticatedSession(getState())) && navigator.onLine !== false
  if (!canContinue()) return
  if (draining) return
  if (typeof navigator !== "undefined" && navigator.onLine === false) return

  draining = true
  if (dispatch) dispatch(setIsSyncing(true))

  let syncedAnyReportData = false
  let allSucceeded = true
  const resolvedIds = new Map()
  const failedEntityIds = new Set()
  const rememberId = async (temporaryId, actualId) => {
    if (!temporaryId || !actualId) return
    resolvedIds.set(temporaryId, actualId)
    if (dispatch) dispatch(rememberResolvedIds({ [temporaryId]: actualId }))
    await putCachedData("sync:resolved-ids", Object.fromEntries(resolvedIds))
  }
  // A create response can predate edits saved later while offline. Overlay
  // those still-queued patches before exposing the server ID to the UI.
  const withPendingEdits = async (record, entityType, aliases) => {
    const ids = new Set([record?._id, ...aliases].filter(Boolean))
    let merged = record
    for (const queued of await getAllOutboxItems()) {
      if (queued.meta?.entityType !== entityType || queued.meta?.requestType !== `${entityType}-update` ||
        ![queued.meta?.entityId, queued.meta?.clientId].some(id => ids.has(id))) continue
      try { merged = mergeSiteData(merged, JSON.parse(queued.body || "{}")) } catch { /* Keep malformed entries for explicit sync error handling. */ }
    }
    return { ...merged, _id: record._id, ...(record.id ? { id: record.id } : {}) }
  }
  const resolveTemporaryIds = (value) => {
    if (typeof value !== "string") return value
    return resolvedIds.get(value) || value
  }
  const resolveQueuedRequest = (item) => {
    const temporaryIds = [...resolvedIds.keys()]
      .filter(Boolean)
    let url = item.url
    for (const temporaryId of temporaryIds) {
      const actualId = resolveTemporaryIds(temporaryId)
      if (actualId !== temporaryId && typeof url === "string") {
        url = url.split(`/${temporaryId}`).join(`/${actualId}`)
      }
    }
    let body = item.body
    if (typeof body === "string") {
      try {
        const parsed = JSON.parse(body)
  const resolveNestedIds = (value) => {
    if (Array.isArray(value)) return value.map(resolveNestedIds)
    if (!value || typeof value !== "object") return value
    return Object.fromEntries(Object.entries(value).map(([key, nested]) => [key,
      ["reportId", "siteId", "observationId", "workId", "entityId", "riskId"].includes(key)
        ? resolveTemporaryIds(nested) : resolveNestedIds(nested)]))
  }
  // Local optimistic identifiers are never valid Mongo ObjectIds. They are
  // carried in outbox metadata for reconciliation, not in API payloads.
  // if (item.type === "observation-create") {
  //   delete parsed._id
  //   delete parsed.id
  //   delete parsed._clientId
  //   delete parsed.clientId
  //   delete parsed._offlineQueued
  // }
  body = JSON.stringify(resolveNestedIds(parsed))
      } catch {
        // Preserve non-JSON request bodies exactly as queued.
      }
    }
    return { ...item, url, body }
  }

  try {
    const savedIds = await getCachedData("sync:resolved-ids")
    if (dispatch) dispatch(rememberResolvedIds(savedIds || {}))
    for (const [temporaryId, actualId] of Object.entries(savedIds || {})) resolvedIds.set(temporaryId, actualId)
    const allItems = await getAllOutboxItems()
    for (const item of allItems.filter((entry) => entry.retryable === false || automatic && (entry.retryCount || 0) >= 3)) {
      for (const id of [...providedIds(item), item.meta?.entityId, item.meta?.clientId]) if (id) failedEntityIds.add(id)
    }
    const items = orderSyncItems(allItems).filter((item) => (!itemId || item.id === itemId) && item.retryable !== false && (!automatic || (item.retryCount || 0) < 3))
    if (dispatch) { dispatch(setOutboxItems(allItems)); dispatch(beginSyncRun(items)) }

    for (const item of items) {
      if (!canContinue()) { allSucceeded = false; break }
      const blockedIds = dependentIds(item).filter((id) => failedEntityIds.has(id) ||
        (String(id).startsWith("offline") || String(id).startsWith("pending_")) && resolveTemporaryIds(id) === id)
      if (blockedIds.length) {
        allSucceeded = false
        const patch = { status: "blocked", lastError: "Waiting for the linked record or image to sync.", retryable: true }
        const updated = await updateOutboxItem(item.id, patch)
        if (dispatch) { if (updated) dispatch(upsertOutboxItem(updated)); dispatch(updateSyncOperation({ id: item.id, ...patch })) }
        for (const id of providedIds(item)) if (id) failedEntityIds.add(id)
        continue
      }
      try {
        const active = await updateOutboxItem(item.id, { status: "syncing" })
        if (!active) continue
        if (dispatch) { dispatch(upsertOutboxItem(active)); dispatch(updateSyncOperation({ id: item.id, status: "syncing" })) }
        const parentId = item.metadata?.entityId || item.meta?.parentEntityId
        if (failedEntityIds.has(parentId) || failedEntityIds.has(item.meta?.entityId)) throw new Error("Waiting for an earlier record change to sync")
        if (item.type === "file-upload") {
          const fileRecord = await replayFileUploadItem(item, {
            resolveId: resolveTemporaryIds,
          })
  
          const actualFileId = fileRecord?._id || fileRecord?.id
          await cacheImageBlob(actualFileId, item.file, fileRecord?.fileUrl)
          if (actualFileId) {
            // The optimistic placeholder file (rendered while offline) is
            // addressed as `pending_${clientId}` by any queued description
            // edit made to it before the upload synced. Resolve both forms
            // so that a later `file-update` item's URL gets the real id.
            await rememberId(item.clientId, actualFileId)
            await rememberId(`pending_${item.clientId}`, actualFileId)
          }
          await deleteOutboxItem(item.id)
          if (dispatch) {
            dispatch(removeOutboxItem(item.id))
            dispatch(addSyncedFile({ file: fileRecord, clientId: item.clientId }))
            // The observation/work screens read images from
            // `currentReportData.files`, a separate copy from the files
            // slice above — without this, that copy keeps the "pending_*"
            // placeholder id after sync, breaking any later edit/delete of
            // the same image.
            dispatch(replaceSyncedReportFile({ file: fileRecord, clientId: item.clientId }))
            dispatch(setFileQueueStatus({ id: item.clientId, name: item.file?.name, status: "uploaded" }))
          }
          syncedAnyReportData = true
          console.log("[v0] Synced queued file upload:", item?.meta?.description || item.file?.name)
        } else if (item.type === "file-clone") {
          const response = await replayOutboxItem(resolveQueuedRequest(item))
          const payload = await response.json()
          const file = payload.data?.[0]
          if (!file?._id) throw new Error("Imported image sync returned no file")
          if (dispatch) {
            dispatch(addSyncedFile({ file, clientId: item.meta.clientId }))
            dispatch(replaceSyncedReportFile({ file, clientId: item.meta.clientId }))
          }
          await deleteOutboxItem(item.id)
          if (dispatch) dispatch(removeOutboxItem(item.id))
          syncedAnyReportData = true
        } else if (item.meta?.entityType === "key-issue-matrix") {
          const resolved = resolveQueuedRequest(item)
          if (String(JSON.parse(resolved.body).observationId).startsWith("offline")) {
            throw new Error("Waiting for the linked observation to sync")
          }
          const response = await replayOutboxItem(resolved)
          const payload = await response.json()
          const matrixItem = payload.data
          if (!matrixItem?._id) throw new Error("Key issue sync returned no item id")
          const preservePending = (await getAllOutboxItems()).some((other) => other.id !== item.id &&
            other.meta?.entityType === "key-issue-matrix" && other.meta?.entityId === item.meta.entityId)
          if (dispatch) dispatch(applySyncedKeyIssue({ item: matrixItem, previousReportId: item.meta.reportId, preservePending }))
          await deleteOutboxItem(item.id)
          if (dispatch) dispatch(removeOutboxItem(item.id))
          syncedAnyReportData = true
        } else if (item.type === "site-create") {
          const response = await retryWithBackoff(() => fetch(`${AUTH_BASE_URL}/site-data`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(item.siteDataInfo || {}),
          }), { retries: 2 })
          await checkSyncResponse(response)
          const payload = await response.json()
          const site = payload?.data?.site || payload?.data || payload?.site
          if (!site?._id) throw new Error("Site creation sync returned no site id")
          await rememberId(`offline-site-${item.clientId}`, site._id)
          await deleteOutboxItem(item.id)
          if (dispatch) {
            dispatch(removeOutboxItem(item.id))
            dispatch(addSyncedSite({ site, clientId: item.clientId }))
          }
          syncedAnyReportData = true
          console.log("[v0] Synced queued site creation:", item?.meta?.description)
        } else if (item.type === "observation-create") {
          // The observation's reportId/siteId may still be the offline
          // placeholder assigned when the parent report/site were created
          // while offline (e.g. `offline-report-<clientId>`). Resolve those
          // before creating the observation, otherwise the server rejects
          // the request with a BSONError instead of creating the record —
          // which also means later queued updates/uploads never get a real
          // observation id to resolve against.
          const response = await replayOutboxItem(resolveQueuedRequest(item))
          const payload = await response.json()
          let observation = payload?.data?.observation || payload?.data
          if (!observation?._id) throw new Error("Observation creation sync returned no observation id")
          await rememberId(item.meta?.clientId, observation._id)
          await rememberId(item.meta?.entityId, observation._id)
          // Old outboxes may have created embedded risks. The compatibility
          // endpoint now stores them separately and returns the migrated list.
          if (observation.keyIssueMatrixItems) {
            const queued = JSON.parse(item.body || "{}")
            for (const risk of observation.keyIssueMatrixItems) {
              const source = (queued.riskMatrixItems || []).find((candidate) =>
                candidate.title === risk.title && candidate.description === risk.description)
              if (source) await rememberId(source._id || source.id, risk._id)
              if (dispatch) dispatch(applySyncedKeyIssue({ item: risk, previousReportId: queued.reportId }))
            }
          }
          await deleteOutboxItem(item.id)
          observation = await withPendingEdits(observation, "observation", [item.meta?.clientId, item.meta?.entityId])
          if (dispatch) {
            dispatch(removeOutboxItem(item.id))
            dispatch(addSyncedObservation({ observation, clientId: item.meta?.clientId || item.meta?.entityId }))
          }
          syncedAnyReportData = true
          console.log("[v0] Synced queued observation creation:", item?.meta?.description)
        } else if (item.type === "report-create") {
          const siteId = resolvedIds.get(item.siteId) || item.siteId
          if (String(siteId || "").startsWith("offline-site-")) {
            throw new Error("Waiting for the parent site to sync")
          }
          const response = await retryWithBackoff(() => fetch(`${AUTH_BASE_URL}/audit-reports`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              siteId,
              templateId: item.templateId,
              templateName: item.templateName,
              reportName: item.reportName,
              projectId: item.projectId,
            }),
          }), { retries: 2 })
          await checkSyncResponse(response)
          const payload = await response.json()
          const report = payload?.data?.auditReport || payload?.data?.report || payload?.auditReport || payload?.data
          if (!report?._id) throw new Error("Report creation sync returned no report id")
          // Section updates queued while the report was offline use the
          // placeholder `offline-report-${clientId}`. Resolve it before the
          // next queued item is replayed.
          await rememberId(`offline-report-${item.clientId}`, report._id)
          await rememberId(item.clientId, report._id)
          await deleteOutboxItem(item.id)
          if (dispatch) {
            dispatch(removeOutboxItem(item.id))
            dispatch(addSyncedReport({ report, clientId: item.clientId }))
          }
          syncedAnyReportData = true
          console.log("[v0] Synced queued report creation:", item?.meta?.description)
        } else if (item.meta?.requestType === "work-create") {
          // A work item's reportId may still be an offline placeholder if
          // its parent report was also created while offline — resolve it
          // before creating the work item, and capture the real work id so
          // any queued update/delete/file-upload targeting the offline
          // "offline-work-..." clientId resolves correctly below.
          const response = await replayOutboxItem(resolveQueuedRequest(item))
          const payload = await response.json()
          let work = payload?.data?.work || payload?.data
          if (!work?._id) throw new Error("Work creation sync returned no work id")
          await rememberId(item.meta?.clientId, work._id)
          await rememberId(item.meta?.entityId, work._id)
          await deleteOutboxItem(item.id)
          work = await withPendingEdits(work, "work", [item.meta?.clientId, item.meta?.entityId])
          if (dispatch) {
            dispatch(removeOutboxItem(item.id))
            dispatch(addSyncedWork({ work, clientId: item.meta?.clientId || item.meta?.entityId }))
          }
          syncedAnyReportData = true
          console.log("[v0] Synced queued work creation:", item?.meta?.description)
        } else if (item.meta?.entityType === "observation" && item.meta?.requestType === "observation-update") {
          const resolvedItem = resolveQueuedRequest(item)
          const response = await replayOutboxItem(resolvedItem)
          const payload = await response.json()
          let observation = payload?.data?.observation || payload?.data
          if (observation?.keyIssueMatrixItems) {
            const queued = JSON.parse(item.body || "{}")
            for (const risk of observation.keyIssueMatrixItems) {
              if (queued.riskId && ["create", "add"].includes(queued.action) && queued.riskData?.title === risk.title) {
                await rememberId(queued.riskId, risk._id)
              }
              if (dispatch) dispatch(applySyncedKeyIssue({ item: risk, previousReportId: item.meta?.reportId }))
            }
          }
          await deleteOutboxItem(item.id)
          if (dispatch && observation?._id) {
            observation = await withPendingEdits(observation, "observation", [item.meta?.clientId, item.meta?.entityId])
            dispatch(removeOutboxItem(item.id))
            dispatch({ type: "observations/addSyncedObservation", payload: { observation, clientId: item.meta.entityId } })
          }
          syncedAnyReportData = true
          console.log("[v0] Synced queued observation update:", item?.meta?.description)
        }
         else if (item.meta?.entityType === "work" && item.meta?.requestType === "work-update") {
          // Work updates can be queued against the offline work id. Resolve
          // the URL/body first, then reconcile the server response back to
          // the same optimistic work item so later queued image uploads use
          // the real work _id as their entityId.
          const resolvedItem = resolveQueuedRequest(item)
          const response = await replayOutboxItem(resolvedItem)
          const payload = await response.json()
          let work = payload?.data?.work || payload?.data
          await deleteOutboxItem(item.id)
          if (dispatch && work?._id) {
            work = await withPendingEdits(work, "work", [item.meta?.clientId, item.meta?.entityId])
            dispatch(removeOutboxItem(item.id))
            dispatch({
              type: "works/addSyncedWork",
              payload: { work, clientId: item.meta?.clientId || item.meta?.entityId },
            })
            await rememberId(item.meta?.clientId, work._id)
            await rememberId(item.meta?.entityId, work._id)
          }
          syncedAnyReportData = true
          console.log("[v0] Synced queued work update:", item?.meta?.description)
        }
         else {
          // A report can be created offline before the server assigns its id.
          // Resolve that placeholder in every queued section/site-data/file
          // update before replaying it.
          await replayOutboxItem(resolveQueuedRequest(item))
          await deleteOutboxItem(item.id)
          if (dispatch) dispatch(removeOutboxItem(item.id))
          syncedAnyReportData = true
          console.log("[v0] Synced queued request:", item?.meta?.description || item.url)
        }
        if (dispatch) dispatch(updateSyncOperation({ id: item.id, status: "synced", lastError: null }))
      } catch (error) {
        for (const id of [...providedIds(item), item.meta?.entityId, item.meta?.clientId, item.clientId]) if (id) failedEntityIds.add(id)
        allSucceeded = false
        const retryCount = (item.retryCount || 0) + 1
        const validation = error.status >= 400 && error.status < 500 && ![408, 429].includes(error.status)
        const interrupted = !canContinue()
        const patch = { status: interrupted ? "pending" : "failed", retryCount: interrupted ? (item.retryCount || 0) : retryCount,
          retryable: !validation, errorKind: validation ? "validation" : "network",
          lastError: validation ? `${error.message}. Edit this item to correct the problem, then save it again.` : error?.message || "Sync failed" }
        const updated = await updateOutboxItem(item.id, patch)
        if (dispatch) dispatch(updateSyncOperation({ id: item.id, ...patch }))
        if (dispatch && updated) dispatch(upsertOutboxItem(updated))
        if (dispatch && item.type === "file-upload") {
          dispatch(setFileQueueStatus({ id: item.clientId, name: item.file?.name, status: "failed", error: error?.message }))
        }
        console.log("[v0] Queued request retained:", error?.message)
        if (interrupted) break
      }
    }

    // Reconciliation on reconnect: the queued items were applied optimistically
    // against locally-held state (which may have drifted from the server, e.g.
    // another device edited the same report, or server-side validation adjusted
    // a value). Once everything has actually round-tripped through the server,
    // re-fetch the authoritative data so progress percentages shown on the
    // dashboard, the report list, and any open report view all agree with what
    // the server now has — instead of continuing to trust the optimistic merge.
    const pendingAfterSync = await getAllOutboxItems()
    if (dispatch && syncedAnyReportData && allSucceeded && pendingAfterSync.length === 0) {
      await dispatch(fetchAllReportsWithData())
    }
  } finally {
    draining = false
    if (dispatch) {
      dispatch(setIsSyncing(false))
      // Keep the explicit Sync now prompt visible whenever an item failed or
      // was left behind, including intermittent reconnect failures.
      const remainingItems = await getAllOutboxItems()
      dispatch(setOutboxItems(remainingItems))
      dispatch(setHasPendingSyncPrompt(remainingItems.length > 0))
      if (remainingItems.length === 0 && syncedAnyReportData && allSucceeded) {
        const completedAt = Date.now()
        dispatch(setLastSyncSucceededAt(completedAt))
        await putCachedData("sync:last-success", completedAt)
      }
    }
  }
}

export function startSyncManager(dispatch, getState, subscribe) {
  let disposed = false
  let retryTimer
  const canSync = () => !disposed && getState && authenticatedSession(getState()) && navigator.onLine !== false
  const run = async () => {
    if (!canSync() || draining) return
    try {
      await drainOutbox(dispatch, { getState, automatic: true })
      const items = await getAllOutboxItems()
      const eligible = items.filter((item) => item.retryable !== false && item.status !== "blocked" && (item.retryCount || 0) < 3)
      if (canSync() && eligible.length) {
        const attempt = Math.max(...eligible.map((item) => item.retryCount || 0))
        retryTimer = setTimeout(run, Math.min(1000 * 2 ** attempt, 10000))
      }
    } catch (error) { console.error("Unable to read the sync queue", error) }
  }
  getAllOutboxItems().then(async (items) => {
    if (disposed) return
    dispatch(setOutboxItems(items))
    const pendingSites = items.filter(item => item.type === "site-create")
    if (pendingSites.length && getState && authenticatedSession(getState())) {
      const restored = await dispatch(hydratePendingSites(pendingSites))
      if (restored.meta?.requestStatus === "rejected") {
        console.error("Unable to restore locally created sites", restored.error?.message)
      }
      if (disposed) return
    }
    const lastSuccess = await getCachedData("sync:last-success")
    if (!disposed && lastSuccess) dispatch(setLastSyncSucceededAt(lastSuccess))
    if (!disposed && items.length) await run()
  }).catch((error) => console.error("Unable to load the sync queue", error))
  dispatch(setOnlineStatus(navigator.onLine))
  if (listenersRegistered || typeof window === "undefined") return () => { disposed = true; clearTimeout(retryTimer) }
  listenersRegistered = true
  const handleOnline = () => { dispatch(setOnlineStatus(true)); clearTimeout(retryTimer); run() }
  const handleOffline = () => { dispatch(setOnlineStatus(false)); clearTimeout(retryTimer) }
  let previousQueue = getState?.()?.offline?.outboxItems
  const unsubscribe = subscribe?.(() => {
    const items = getState()?.offline?.outboxItems
    if (items === previousQueue) return
    previousQueue = items
    if (canSync() && !draining && items?.some((item) => item.status === "pending" && item.retryable !== false)) {
      clearTimeout(retryTimer)
      retryTimer = setTimeout(run, 250)
    }
  })
  window.addEventListener("online", handleOnline)
  window.addEventListener("offline", handleOffline)
  return () => {
    disposed = true
    unsubscribe?.()
    clearTimeout(retryTimer)
    window.removeEventListener("online", handleOnline)
    window.removeEventListener("offline", handleOffline)
    listenersRegistered = false
  }
}

export function retryOutboxItemNow(dispatch, options) {
  return drainOutbox(dispatch, options)
}
